<template>
    <div id="users-view">
        <data-table
            v-if="!fetchingOrganization"
            :title="this.tableTitle"
            delete-button-text="selected users"
            :headers="headers"
            :get-items-handler="getUsers"
            :get-delete-handler="deleteUser"
            :get-prepared-data-to-save="prepareDataToSave"
            :reset-filters-handler="resetFilters"
            has-filters
            :deletable="hasAllRoles(['UserDelete'])"
        >
            <template v-slot:tableFilters>
                <users-filters
                    :filters="filters"
                ></users-filters>
            </template>
            <template v-slot:items="item">
                <td>{{ item.props.item.userName }}</td>
                <td>{{ item.props.item.firstName }}</td>
                <td>{{ item.props.item.lastName }}</td>
                <td>{{ item.props.item.phoneNumber }}</td>
                <td>{{ item.props.item.email }}</td>
                <td>{{ item.props.item.lastLoginAttempt ? dateFormat(item.props.item.lastLoginAttempt) : 'never' }}</td>
                <td v-if="hasAllRoles(['UserUpdate'])" class="text-center">
                    <router-link :to="{ name: 'editUserDetails', params: { id: item.props.item.id } }">
                        <sp-button design-type="table-button" icon="edit"></sp-button>
                    </router-link>
                </td>
            </template>

            <template v-slot:createButton v-if="hasAllRoles(['UserAdd'])">
                <slot name="createUserButton">
                    <router-link to="/settings/users/create" class="no-underline">
                        <sp-button sp-class="no-padding-right" icon="user" text="Add user"></sp-button>
                    </router-link>
                </slot>
            </template>
        </data-table>
        <sp-loader v-if="isLoading"></sp-loader>
    </div>
</template>

<script>
import SpButton from '@/components/forms/Button'
import DataTable from '@/views/_components/DataTable'
import UserApi from '@/api/UserApi'
import UsersFilters from '@/views/Users/components/UsersFilters'
import SpLoader from '@/components/atoms/Loader'
import toast from '@/plugins/izitoast'
import { mapActions, mapGetters } from 'vuex'
import OrganizationApi from '@/api/OrganizationApi'
import { setTableFiltersToLocalStorage, getTableFiltersFromLocalStorage } from '@/helpers/StorageHelper'
import { formatDate } from '@/helpers/Utils'
const userApi = new UserApi()
const organizationApi = new OrganizationApi()

export default {
    name: 'UsersTable',
    components: {
        SpButton,
        UsersFilters,
        DataTable,
        SpLoader
    },
    props: {
        role: {
            type: String,
            required: false,
            default: null
        },
        tableTitle: {
            type: String,
            required: false,
            default: null
        }
    },
    data () {
        return {
            headers: [
                {
                    text: 'Username',
                    sortable: true,
                    value: 'userName'
                },
                {
                    text: 'First name',
                    sortable: true,
                    value: 'firstName'
                },
                {
                    text: 'Last name',
                    sortable: true,
                    value: 'lastName'
                },
                {
                    text: 'Phone number',
                    sortable: true,
                    value: 'phoneNumber'
                },
                {
                    text: 'E-mail',
                    sortable: true,
                    value: 'email'
                },
                {
                    text: 'Last login attempt',
                    sortable: false,
                    value: 'lastLoginAttempt'
                },
                {
                    text: 'Edit',
                    value: 'isEditable',
                    sortable: false,
                    width: '70px',
                    class: !this.hasAllRoles(['UserUpdate']) ? 'd-none' : ''
                },
                {
                    text: 'Delete',
                    value: 'isDeletable',
                    sortable: false,
                    width: '70px',
                    class: !this.hasAllRoles(['UserDelete']) ? 'd-none' : ''
                }
            ],
            isLoading: false,
            totalResults: 0,
            fetchingOrganization: false,
            fetchingUsers: false,
            filters: {
                userName: null,
                email: null,
                firstName: null,
                lastName: null,
                organizations: null,
                fleets: null
            }
        }
    },
    mounted () {
        this.applySavedFilters()
        if (this.fleets) {
            this.filters.fleets = this.fleets
        }
    },
    computed: {
        ...mapGetters('authModule', [
            'getActiveOrganization',
            'getIsImpersonateActive'
        ]),
        ...mapGetters('managementTables', [
            'getUserFilters'
        ]),
        isUserTableReady () {
            return !this.fetchingOrganization && !this.fetchingUsers
        }
    },
    methods: {
        ...mapActions('managementTables', [
            'setUserFilters',
            'getLocalStorageFilters'
        ]),
        async getUsers (page, perPage, userName, orderBy, email) {
            this.isLoading = true

            const response = await userApi.getUsers({
                page: page,
                perPage: perPage,
                userName: this.filters.userName ? this.filters.userName : null,
                email: this.filters.email ? this.filters.email : null,
                firstName: this.filters.firstName ? this.filters.firstName : null,
                lastName: this.filters.lastName ? this.filters.lastName : null,
                organizations: this.filters.organizations ? this.filters.organizations : null,
                fleets: this.filters.fleets ? this.filters.fleets : null,
                orderBy: orderBy
            })
            this.totalResults = response.payload.totalResults

            if (!this.getIsImpersonateActive) {
                this.setUserFilters(this.filters)
                setTableFiltersToLocalStorage('users', this.filters)
            }

            this.isLoading = false

            if (!response.isSuccess) {
                this.isLoading = false
                return
            }
            return {
                total: response.payload.totalResults,
                items: response.payload.items
            }
        },
        async applySavedFilters () {
            if (!this.getIsImpersonateActive) {
                const savedFilters = getTableFiltersFromLocalStorage('users')
                if (savedFilters) {
                    this.filters = { ...savedFilters }
                    return
                }
            } else {
                this.filters.userName = null
                this.filters.email = null
                this.filters.firstName = null
                this.filters.lastName = null
                this.filters.organizations = null
                this.filters.fleets = null
            }
            await this.getOrganization()
        },
        async getOrganization () {
            this.fetchingOrganization = true
            const response = await organizationApi.getOrganizationById(this.getActiveOrganization)
            if (!response.isSuccess) {
                this.fetchingOrganization = false
                return
            }
            this.filters.organizations = response.payload
            this.fetchingOrganization = false
        },
        async deleteUser (user) {
            const response = await userApi.deleteUser(user.id)

            if (!response.isSuccess) {
                return
            }

            toast.success(`User ${user.userName} successfully deleted.`)
        },
        async prepareDataToSave (search) {
            const dataToSave = []

            const response = await userApi.getUsers({
                userName: this.filters.userName ? this.filters.userName : null,
                email: this.filters.email ? this.filters.email : null,
                firstName: this.filters.firstName ? this.filters.firstName : null,
                lastName: this.filters.lastName ? this.filters.lastName : null,
                organizations: this.filters.organizations ? this.filters.organizations : null,
                fleets: this.filters.fleets ? this.filters.fleets : null
            })

            response.payload.items.forEach(item => {
                dataToSave.push({
                    userName: item.userName ? item.userName : ' ',
                    firstName: item.firstName ? item.firstName : ' ',
                    lastName: item.lastName ? item.lastName : ' ',
                    phoneNumber: item.phoneNumber ? item.phoneNumber : ' ',
                    email: item.email ? item.email : ' ',
                    lastLogin: item.lastLoginAttempt ? formatDate(item.lastLoginAttempt) : 'never'
                })
            })
            return dataToSave
        },
        resetFilters () {
            this.filters = {
                userName: null,
                email: null,
                organizations: this.getIsImpersonateActive ? this.filters.organizations : null,
                fleets: null
            }
        },
        dateFormat (date) {
            return formatDate(date)
        }
    }
}
</script>

<style scoped>

</style>
